import { getModalList } from "@/components/HBModal/ModalWrap";
import { SHORTCUT } from "@/constants/shortcut";
import useAppStore from "@/context/store";
import isEditingElement from "@/utils/isEditingElement";
import { useKeyPress } from "ahooks";
import { BackTop, Popover } from "antd";
import scrollTo from "antd/lib/_util/scrollTo";
import classNames from "classnames";
import _ from "lodash";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback, useMemo } from "react";
import { When } from "react-if";
import initUdesk from "../../utils/initUdesk";
import BoardModal from "../BoardModal";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import { useLogin } from "../LoginModal";
import PageSettingModal from "../PageSettingModal";
import { PinUploaderModal } from "../PinCreator";
import PinSuccessMessage from "../PinSuccessMessage";
import ShortcutModal from "../ShortcutModal";
import styles from "./index.module.less";

export default function SideButtonList() {
  const {
    headerHeight,
    user,
    config = {},
  } = useAppStore(({ headerHeight, user, config }) => ({
    headerHeight,
    user,
    config,
  }));
  const router = useRouter();
  const { login } = useLogin();

  const onCreateBoard = useCallback(async () => {
    if (!user) {
      const result = await login();
      if (!result) {
        return;
      }
    }

    BoardModal.open({
      name: "boardModal",
      onOk: (board) => {
        router.push(`/boards/${board.board_id}`);
      },
    });
  }, [login, router, user]);

  const onCreatePin = useCallback(async () => {
    if (!user) {
      const result = await login();
      if (!result) {
        return;
      }
    }

    if (!user?.bindings?.wechat && !user?.tel) {
      HBMessage.error(
        "根据《互联网用户账号信息管理规定》，上传采集需绑定微信或手机号"
      );
      router.push("/settings?tab=account");
      return;
    }

    PinUploaderModal.open({
      name: "pinUploaderModal",
      onOk: (board, list, hasAiImg) => {
        PinSuccessMessage.open({
          pin: { ...board?.pins?.shift(), board },
          extra: hasAiImg
            ? { link: "/ai", subtitle: "AI作品已发布到", title: "AI圈" }
            : null,
          hideButton: true,
        });
      },
    });
  }, [login, router, user]);

  const onHelp = useCallback(() => {
    initUdesk(user, () => {
      window.ud && window.ud.showPanel();
    });
  }, [user]);

  const onShortcut = useCallback(() => {
    ShortcutModal.open();
  }, []);

  const onGo = useCallback(
    (e) => {
      e.preventDefault();

      document.cookie = `app=elder;path=/;max-age=${
        config?.switchTimeout || 604800
      }`; // 默认7天
      const path = router.pathname;

      if (path.startsWith("/user")) {
        const { tabId, urlname } = router.query;
        if (path === "/user/[urlname]/following/[tabId]") {
          if (tabId === "job") {
            window.location.href = "/settings/#set_profile";
          } else if (tabId === "board") {
            window.location.href = window.location.pathname
              .slice(5)
              .replace("board", "boards");
          } else if (tabId === "topic") {
            window.location.href = "/discovery";
          } else if (tabId === "domain") {
            window.location.href = `/${urlname}`;
          } else {
            window.location.href = window.location.pathname.slice(5);
          }
        } else {
          window.location.href = window.location.pathname.slice(5);
        }
      } else if (path === "/follow" || path === "/guide") {
        window.location.href = "/home";
      } else if (path === "/search") {
        const { type, subtype, urlname, ...otherQuery } = router.query;
        if (type === "pin") {
          window.location.href = `/search/?${qs.stringify(otherQuery)}`;
        } else if (type === "board") {
          window.location.href = `/search/boards?${qs.stringify(otherQuery)}`;
        } else if (urlname) {
          // 是否是该用户
          if (urlname === user.urlname) {
            const name = urlname || user.urlname;
            if (!subtype || subtype === "pin") {
              window.location.href = `/${name}/pins/?${qs.stringify(
                otherQuery
              )}`;
            } else {
              window.location.href = `/${name}/boards/?${qs.stringify(
                otherQuery
              )}`;
            }
          } else {
            window.location.href = `/search/people?${qs.stringify(otherQuery)}`;
          }
        } else {
          window.location.href = `/search/?${qs.stringify(otherQuery)}`;
        }
      } else if (path === "/discovery/[categoryId]/materials") {
        window.location.href = "/discovery";
      } else {
        window.location.reload();
      }
    },
    [router, user, config?.switchTimeout]
  );

  const onSetting = useCallback(() => {
    if (router.pathname === "/settings" && router.query?.tab === "preference") {
      HBMessage.info("您已经在偏好设置页了");
      return;
    }
    PageSettingModal.open();
  }, [router]);

  // 回到顶部
  useKeyPress(
    SHORTCUT.TO_TOP,
    () => {
      const editing = isEditingElement();
      if (editing) {
        return false;
      }
      // 判断是否打开采集详情弹窗，如果打开就滚动弹窗
      const pinModal = document.querySelector(".pinDetailModal");
      if (pinModal) {
        scrollTo(0, {
          getContainer: () => pinModal,
        });
      } else {
        scrollTo(0);
      }
    },
    {
      exactMatch: true,
    }
  );
  // 快捷键提示
  useKeyPress(
    SHORTCUT.SHORTCUT_TIP,
    () => {
      const editing = isEditingElement();
      if (editing) {
        return false;
      }

      const modalList = getModalList();
      const modal = modalList.find((modal) => modal.name === "shortcutModal");
      if (modal) {
        modal.close();
      } else {
        ShortcutModal.open({
          name: "shortcutModal",
        });
      }
    },
    {
      exactMatch: true,
    }
  );
  // 新建画板
  useKeyPress(
    SHORTCUT.CREATE_BOARD,
    () => {
      const editing = isEditingElement();
      if (editing) {
        return false;
      }
      const modalList = getModalList();
      const modal = modalList.find((modal) => modal.name === "boardModal");
      if (modal) {
        modal.close();
      } else {
        onCreateBoard();
      }
    },
    {
      exactMatch: true,
    }
  );
  // 上传采集
  useKeyPress(
    SHORTCUT.UPLOAD_PIN,
    () => {
      const editing = isEditingElement();
      if (editing) {
        return false;
      }

      const modalList = getModalList();
      const modal = modalList.find(
        (modal) => modal.name === "pinUploaderModal"
      );
      if (modal) {
        modal.close();
      } else {
        onCreatePin();
      }
    },
    {
      exactMatch: true,
    }
  );
  // 向下滚动
  useKeyPress(
    SHORTCUT.SCROLL_DOWN,
    _.throttle(
      () => {
        const editing = isEditingElement();
        if (editing) {
          return false;
        }
        // 判断是否打开采集详情弹窗，如果打开就滚动弹窗
        const pinModal = document.querySelector(".pinDetailModal");
        if (pinModal) {
          pinModal.scrollBy({
            top: window.innerHeight - headerHeight,
            behavior: "smooth",
          });
        } else {
          window.scrollBy({
            top: window.innerHeight - headerHeight,
            behavior: "smooth",
          });
        }
      },
      700,
      { trailing: false }
    ),
    {
      exactMatch: true,
    }
  );
  // 线上滚动
  useKeyPress(
    SHORTCUT.SCROLL_UP,
    _.throttle(
      () => {
        const editing = isEditingElement();
        if (editing) {
          return false;
        }
        // 判断是否打开采集详情弹窗，如果打开就滚动弹窗
        const pinModal = document.querySelector(".pinDetailModal");
        if (pinModal) {
          pinModal.scrollBy({
            top: headerHeight - window.innerHeight,
            behavior: "smooth",
          });
        } else {
          window.scrollBy({
            top: headerHeight - window.innerHeight,
            behavior: "smooth",
          });
        }
      },
      700,
      { trailing: false }
    ),
    {
      exactMatch: true,
    }
  );

  const helpContent = useMemo(() => {
    return (
      <div className={styles.menu}>
        <a
          className={styles.menuItem}
          href="/switch.html"
          onClick={onGo}
          data-gd-click="switch_version"
          data-button-name="切换旧版"
          rel="nofollow"
        >
          <HBIcon className={styles.icon} type="keyboard_return" />
          <span className={styles.content}>返回旧版</span>
        </a>
        <a
          className={styles.menuItem}
          target="_blank"
          href="//s.gd-share.cn/4fsy"
          rel="nofollow noreferrer"
        >
          <HBIcon className={styles.icon} type="lightbulb" />
          <span className={styles.content}>新手入门</span>
        </a>
        <a
          className={styles.menuItem}
          target="_blank"
          href={`//support.qq.com/products/30852/?customInfo=https://huaban.com/${user?.urlname}`}
          rel="nofollow noreferrer"
        >
          <HBIcon className={styles.icon} type="ic_help" />
          <span className={styles.content}>帮助中心</span>
        </a>
        <a
          className={styles.menuItem}
          target="_blank"
          href="//support.qq.com/products/30852/change-log"
          rel="nofollow noreferrer"
        >
          <HBIcon className={styles.icon} type="bookmark_fill" />
          <span className={styles.content}>更新日志</span>
        </a>
        <When condition={router.pathname !== "/materials"}>
          <a
            className={styles.menuItem}
            href="/vip/consult?leads_entrance=OIf11P1MQ"
            target="_blank"
            rel="nofollow noreferrer"
            data-gd-click="button_click"
            data-button-name="企业咨询"
          >
            <HBIcon className={styles.icon} type="customize" />
            <span className={styles.content}>企业咨询</span>
          </a>
        </When>
        <a className={styles.menuItem} onClick={onHelp}>
          <HBIcon className={styles.icon} type="ic_severice" />
          <span className={styles.content}>在线客服</span>
        </a>
        <a
          className={styles.menuItem}
          target="_blank"
          href="//huaban.com/about/contact"
          rel="nofollow noreferrer"
        >
          <HBIcon className={styles.icon} type="ic_ad" />
          <span className={styles.content}>商务合作</span>
        </a>
      </div>
    );
  }, [onGo, user, router, onHelp]);

  const pageSettingContent = useMemo(() => {
    return (
      <div className={styles.menu}>
        <a
          className={styles.menuItem}
          onClick={onSetting}
          data-gd-click="button_click"
          data-button-name="偏好设置"
          data-content-source="侧边工具栏"
        >
          <HBIcon className={styles.icon} type="pagesetting" />
          <span className={styles.content}>偏好设置</span>
        </a>
        <a className={styles.menuItem} onClick={onShortcut}>
          <HBIcon className={styles.icon} type="keyboard" />
          <span className={styles.content}>快捷键说明</span>
        </a>
      </div>
    );
  }, [onSetting, onShortcut]);

  return (
    <>
      <div className={styles.list}>
        <BackTop className={classNames([styles.back, styles.item])}>
          <div className={styles.button}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
          </div>
        </BackTop>
        <When condition={router.pathname === "/materials"}>
          <a
            className={classNames([styles.button, styles.item])}
            title="企业咨询"
            href="/vip/consult?leads_entrance=5tLl54mg8"
            target="_blank"
            data-gd-click="button_click"
            data-button-name="企业咨询"
          >
            <HBIcon className={styles.icon} type="customize" />
          </a>
        </When>
        <Popover
          placement="leftBottom"
          content={pageSettingContent}
          overlayClassName={styles.popover}
          getPopupContainer={(triggerNode) => triggerNode}
        >
          <div
            className={classNames([styles.button, styles.item])}
            title="控制"
          >
            <HBIcon className={styles.icon} type="tune" />
          </div>
        </Popover>
        <Popover
          placement="leftBottom"
          content={helpContent}
          overlayClassName={styles.popover}
          getPopupContainer={(triggerNode) => triggerNode}
        >
          <div
            className={classNames([styles.button, styles.item])}
            title="帮助"
          >
            <HBIcon className={styles.icon} type="connect" />
          </div>
        </Popover>
      </div>
    </>
  );
}
